import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

export default class extends Controller {
  static values = {
    time: Boolean,
    noDate: Boolean,
    mode: String,
    format: String,
    valueFormat: String,
    clearButton: Boolean,
    minDate: String
  }
  static targets = [
    'startDatetime', 'endDatetime', 'control'
  ]

  connect() {
    let stimulus = this

    this.flatpickr = flatpickr(this.targetElement(), {
      enableTime: stimulus.enableTime(),
      noCalendar: stimulus.noCalendar(),
      mode: stimulus.mode(),
      dateFormat: stimulus.dateFormat(),
      minDate: stimulus.minDateValue || undefined,
      onChange: function(selectedDates) {
        stimulus.setValues(selectedDates)
        stimulus.dispatchDateRangeEvent(selectedDates)
      }
    })

    const currentValue = this.targetElement().value && this.targetElement().value.trim()
    if (currentValue) {
      this.flatpickr.setDate(currentValue, false)
    }

    if (this.clearButtonValue) {
      this.addClearButton()
    }
  }

  targetElement() {
    return this.hasControlTarget ? this.controlTarget : this.element
  }

  disconnect() {
    this.flatpickr.destroy()
  }

  enableTime() {
    return this.timeValue || false
  }

  noCalendar() {
    return this.noDateValue || false
  }

  mode() {
    return this.modeValue || 'single'
  }

  dateFormat() {
    return this.formatValue || 'm/d/Y'
  }

  setValues(selectedDates) {
    if (this.modeValue == 'range') {
      this.setStartAndEndDates(selectedDates)
    } else {
      this.setSingleDate(selectedDates)
    }
  }

  setStartAndEndDates(selectedDates) {
    if (!(this.hasStartDatetimeTarget && this.hasEndDatetimeTarget)) {
      return
    }

    if (selectedDates.length === 2) {
      this.startDatetimeTarget.value = this.rangeValue(selectedDates[0])
      this.endDatetimeTarget.value = this.rangeValue(selectedDates[1])
    } else if (selectedDates.length === 0) {
      this.startDatetimeTarget.value = null
      this.endDatetimeTarget.value = null
    }
  }

  // A range bound as written to the start/end targets: the `valueFormat` (a flatpickr format) when given,
  // else the Date's toDateString().
  rangeValue(date) {
    return this.valueFormatValue ? this.flatpickr.formatDate(date, this.valueFormatValue) : date.toDateString()
  }

  setSingleDate(selectedDates) {
    if (selectedDates.length === 1) {
      const formatted = this.flatpickr.formatDate(selectedDates[0], this.dateFormat())

      if (this.hasStartDatetimeTarget) {
        this.startDatetimeTarget.value = formatted
      } else {
        this.targetElement().value = formatted
      }
    } else if (selectedDates.length === 0) {
      if (this.hasStartDatetimeTarget) {
        this.startDatetimeTarget.value = null
      } else {
        this.targetElement().value = ''
      }
    }
  }

  dispatchDateRangeEvent(selectedDates) {
    if (this.modeValue == 'range' && (selectedDates.length === 2 || selectedDates.length === 0)) {
      this.targetElement().dispatchEvent(new CustomEvent('date-range-selected'))
    }
  }

  addClearButton() {
    const control = this.targetElement()
    const parent = control.parentNode
    if (!parent) return

    parent.classList.add("relative")
    control.classList.add("pr-10")

    const button = document.createElement("button")
    button.type = "button"
    button.className = "absolute right-3 top-1/2 -translate-y-1/2 text-gray-350 hover:text-gray-29 focus:outline-none"
    button.setAttribute("aria-label", "Clear date")
    button.innerHTML = `
      <span class="sr-only">Clear</span>
      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z" clip-rule="evenodd" />
      </svg>
    `
    button.addEventListener("click", () => {
      if (this.flatpickr) {
        this.flatpickr.clear()
      } else {
        control.value = ""
      }

      if (this.hasStartDatetimeTarget) {
        this.startDatetimeTarget.value = null
      }

      if (this.hasEndDatetimeTarget) {
        this.endDatetimeTarget.value = null
      }
    })

    parent.appendChild(button)
  }
}
